import { useEffect, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { AccountTabWrapper, HideProgressWrapper } from './style';
import { Avatar, Typography } from '@mui/material';
import {
  parseTab,
  parseTabValue,
  isTaskModalTab,
  DrawerTab,
} from 'context/accountDrawer/types';
import { useTaskViewPortal } from './useTaskViewPortal';
import { useAccountDrawer } from 'context/accountDrawer';
import { colors } from 'crono-fe-common/theme';
import { useHideTaskConstruction } from './hideTaskConstructionContext';
import { ReactComponent as IconPrepareNextTask } from 'crono-fe-common/icons/Preparing_next_task.svg';
import IconMContact from 'crono-fe-common/icons/Icon-M-Contact';
import IconPriceTag from 'crono-fe-common/icons/Icon-Pricetag';
import AccountTabTab from './tab';
import { AccountTabsContainer } from './tab/style';
import { useAuth } from 'context/auth';
import UpgradeLabel from 'components/UpgradeElements/upgradeLabel';
import CompanyDeletedDrawerView from './accountDeletedDrawerView';
import { GenericView } from './genericView/GenericView';
import { TabValue, getModalTabConfigs } from './modalTabConfig';
import { useBeforeUnload } from 'react-router-dom';
import {
  CloseMdIcon,
  IconButton,
  BuildingMdIcon,
  TimelineMdIcon,
  CloseSmIcon,
  SplitViewSmIcon,
  Separator,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Badge,
  DotSpinner,
} from '@crono-one/design-system';
import { getImageFromUrl, stringAvatarAccount } from 'crono-fe-common/utils';
import { MeetingView } from './meetingView/MeetingView';

// Lazy loaded components
const ContactsView = lazy(() => import('./contactView'));
const TimelineView = lazy(() => import('./timelineView'));
const EmailView = lazy(() => import('./emailView'));
const AccountView = lazy(() => import('./accountView'));
const CallLogView = lazy(() => import('./callLogView'));
const LinkedinView = lazy(() => import('./linkedinView/index'));
const OpportunitiesView = lazy(() => import('./opportunityView'));
const CallView = lazy(() => import('./callView'));
const InMailView = lazy(() => import('./inmailView'));

const AccountDrawer = () => {
  const { user, hasOpportunities, showUpgradeBadges, openPaywallIfNeeded } =
    useAuth();
  const {
    account,
    selectedAccountId,
    selectedProspectId,
    isLoadingGetAccount,
    currentAccountNotExisting,
    closeDrawer: close,
    handleRemoveModal,
    updateDrawerContent,
    drawerContent,
    callNeedsDiscardConfirm,
    setCallViewIsDirty,
    openDiscardCallModal,
    taskSplitPortalNode,
    linkedinTabLabel,
    setLinkedinTabLabel,
  } = useAccountDrawer();
  const {
    selectedThreadId,
    callOnSuccess,
    suggestionId,
    defaultOpenTemplateTab,
    activity,
    initialPhoneSelected,
    taskSessionId,
  } = drawerContent || {};

  const value = parseTab(drawerContent?.tab);

  const setValue = (tabValue: TabValue) => {
    updateDrawerContent({ tab: parseTabValue(tabValue) });
  };

  const modalVisible = drawerContent?.modalVisible ?? null;
  const isTaskSplitView =
    isTaskModalTab(modalVisible ?? undefined) &&
    !!drawerContent?.isTaskSplitView;

  // All four are called unconditionally (rules of hooks) but only the one
  // matching modalVisible below actually mounts anything.
  const emailPortal = useTaskViewPortal({
    isSplitView: isTaskSplitView && modalVisible === 'email',
    splitPortalNode: taskSplitPortalNode,
    visible:
      (isTaskSplitView && modalVisible === 'email') ||
      (!isTaskSplitView &&
        !isLoadingGetAccount &&
        !currentAccountNotExisting &&
        value === TabValue.EMAIL),
  });
  const linkedinPortal = useTaskViewPortal({
    isSplitView: isTaskSplitView && modalVisible === 'linkedin',
    splitPortalNode: taskSplitPortalNode,
    visible:
      (isTaskSplitView && modalVisible === 'linkedin') ||
      (!isTaskSplitView &&
        !isLoadingGetAccount &&
        !currentAccountNotExisting &&
        value === TabValue.LINKEDIN),
  });
  const callPortal = useTaskViewPortal({
    isSplitView: isTaskSplitView && modalVisible === 'call',
    splitPortalNode: taskSplitPortalNode,
    visible:
      (isTaskSplitView && modalVisible === 'call') ||
      (!isTaskSplitView &&
        !isLoadingGetAccount &&
        !currentAccountNotExisting &&
        value === TabValue.CALL),
  });
  const inmailPortal = useTaskViewPortal({
    isSplitView: isTaskSplitView && modalVisible === 'inmail',
    splitPortalNode: taskSplitPortalNode,
    visible:
      (isTaskSplitView && modalVisible === 'inmail') ||
      (!isTaskSplitView &&
        !isLoadingGetAccount &&
        !currentAccountNotExisting &&
        value === TabValue.INMAIL),
  });

  const isModalTab = (tabValue: number) => {
    return [
      TabValue.EMAIL,
      TabValue.LINKEDIN,
      TabValue.CALL,
      TabValue.CALL_LOG,
      TabValue.INMAIL,
      TabValue.GENERIC,
      TabValue.GENERIC_LOG,
      TabValue.MEETING,
    ].includes(tabValue);
  };

  const lastNonModalTab = useRef<DrawerTab>('account');
  useEffect(() => {
    if (!isModalTab(value)) {
      lastNonModalTab.current = parseTabValue(value) as DrawerTab;
    }
  }, [value]);

  const { hideProgressTask, setHideProgressTask } = useHideTaskConstruction();

  //Use effect to remove the hide progress task when the component is unmounted before the complete construction of the task
  useEffect(() => {
    return () => {
      setHideProgressTask(false);
    };
  }, []);

  const task = drawerContent?.task;
  const openScore = drawerContent?.openScore;

  // The drawer selection is set from the task's expanded prospect, which the
  // task payloads that omit it leave empty; the task's own foreign key still
  // names the contact, so the task tabs resolve through it before giving up.
  // The non-task tabs keep the drawer selection, which is legitimately empty
  // when the drawer was opened on a company.
  const taskProspectId = selectedProspectId ?? task?.prospectId ?? null;

  const handleCloseModal = (
    params: {
      askConfirm?: boolean;
    } = {},
  ) => {
    const closeTab = () => {
      //If I'm in the modalTab, I go back to the last visited non-modal tab
      if (isModalTab(value)) {
        updateDrawerContent({
          tab: lastNonModalTab.current,
          modalVisible: undefined,
          isTaskSplitView: false,
        });
      } else {
        handleRemoveModal();
      }
    };

    if (params.askConfirm && callNeedsDiscardConfirm) {
      openDiscardCallModal({
        onConfirm: closeTab,
      });
    } else {
      closeTab();
    }
  };

  const renderModalTab = () => {
    if (!modalVisible) return null;
    // The active task lives in its own drawer while split — no pill for it here.
    if (isTaskSplitView) return null;

    const configs = getModalTabConfigs(linkedinTabLabel);
    const config =
      modalVisible in configs
        ? configs[modalVisible as keyof typeof configs]
        : null;
    if (!config) return null;

    const { label, tabValue, renderIcon } = config;
    const isSelected = value === tabValue;

    return (
      <AccountTabTab
        label={label}
        selected={isSelected}
        onClick={() => setValue(tabValue)}
        endIcon={
          <span className="flex items-center gap-2">
            {isTaskModalTab(modalVisible ?? undefined) && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      label="Open in split view"
                      icon={SplitViewSmIcon}
                      variant="text"
                      size="sm"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        ev.preventDefault();
                        updateDrawerContent({
                          isTaskSplitView: true,
                          tab: lastNonModalTab.current,
                        });
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>Open in split view</TooltipContent>
                </Tooltip>
                <Separator orientation="vertical" className="h-4 w-px" />
              </>
            )}
            <IconButton
              label="Close tab"
              icon={CloseSmIcon}
              variant="text"
              size="sm"
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                handleCloseModal({
                  askConfirm: true,
                });
              }}
            />
          </span>
        }
        icon={renderIcon(isSelected)}
      />
    );
  };

  const renderTabContent = () => {
    // Fixed tabs
    if (value === TabValue.ACCOUNT) {
      return <AccountView close={close} openScore={openScore} />;
    }
    if (value === TabValue.CONTACTS) {
      return (
        <ContactsView
          selectedProspectId={selectedProspectId ?? undefined}
          close={close}
          openScore={openScore}
        />
      );
    }
    if (value === TabValue.TIMELINE) {
      return <TimelineView />;
    }
    if (value === TabValue.OPPORTUNITIES) {
      return <OpportunitiesView />;
    }

    // Dynamic/Variable tabs
    // Email, LinkedIn, Call and InMail are handled below, outside this switch.
    if (modalVisible === 'callLog' && value === TabValue.CALL_LOG) {
      return (
        <CallLogView
          selectedProspectId={selectedProspectId}
          activity={activity}
        />
      );
    }

    if (modalVisible === 'generic' && value === TabValue.GENERIC) {
      return (
        <GenericView
          task={task}
          selectedProspectId={taskProspectId ?? undefined}
          callOnSuccess={callOnSuccess}
        />
      );
    }

    if (
      modalVisible === 'genericLog' &&
      value === TabValue.GENERIC_LOG &&
      activity &&
      'createdDate' in activity
    ) {
      return (
        <GenericView
          activity={activity}
          selectedProspectId={selectedProspectId ?? undefined}
        />
      );
    }

    if (
      modalVisible === 'meeting' &&
      value === TabValue.MEETING &&
      activity &&
      'eventSubtype' in activity
    ) {
      return <MeetingView activity={activity} />;
    }

    return null;
  };

  useBeforeUnload((event) => {
    if (callNeedsDiscardConfirm) {
      event.preventDefault();
    }
  });

  return (
    <AccountTabWrapper id="account-tab-wrapper" padding="0px">
      {hideProgressTask && (
        <HideProgressWrapper isSplitView={isTaskSplitView}>
          <IconButton
            label="Close window"
            icon={CloseMdIcon}
            shape="rounded"
            variant="light"
            tone="lightGray"
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              close();
            }}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              alignContent: 'center',
              width: '100%',
              gap: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  position: 'relative',
                }}
              >
                <IconPrepareNextTask
                  style={{ width: '116px', height: '90px' }}
                />
                <span className="loader" />
              </div>
              <Typography
                fontSize={16}
                fontWeight={600}
                lineHeight={'24px'}
                width={'fit-content'}
              >
                We're preparing the next task
              </Typography>
            </div>
          </div>
        </HideProgressWrapper>
      )}
      <div
        id="account-tab-top-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 18,
          paddingLeft: 18,
          paddingTop: 18,
          backgroundColor:
            isLoadingGetAccount || currentAccountNotExisting
              ? colors.white
              : colors.grey6,
        }}
      >
        <IconButton
          label="Close window"
          icon={CloseMdIcon}
          shape="rounded"
          variant="light"
          tone="lightGray"
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            close();
          }}
        />

        {!isLoadingGetAccount && (
          <div className="flex items-center gap-3">
            <Avatar
              sx={{
                width: 32,
                height: 32,
              }}
              src={
                account?.website ? getImageFromUrl(account.website, null) : ''
              }
            >
              {stringAvatarAccount(account?.name || '')}
            </Avatar>
            <p className="text-2xl font-bold truncate">{account?.name}</p>
          </div>
        )}
      </div>
      {isLoadingGetAccount ? (
        <div className="size-full grid place-content-center">
          <DotSpinner />
        </div>
      ) : currentAccountNotExisting ? (
        <CompanyDeletedDrawerView
          selectedProspectId={selectedProspectId ?? undefined}
        />
      ) : (
        <>
          <div id="tab-header" className="tabs-header-container">
            <AccountTabsContainer compactTabs>
              {renderModalTab()}
              {modalVisible && !isTaskSplitView && (
                <div
                  className="tabs-divider"
                  style={{
                    visibility:
                      value === TabValue.ACCOUNT ? 'hidden' : 'visible',
                  }}
                />
              )}
              <AccountTabTab
                selected={value === TabValue.ACCOUNT}
                onClick={() => setValue(TabValue.ACCOUNT)}
                icon={<BuildingMdIcon />}
                label="Company"
              />
              <div
                className="tabs-divider"
                style={{
                  visibility:
                    value === TabValue.ACCOUNT || value === TabValue.CONTACTS
                      ? 'hidden'
                      : 'visible',
                }}
              />
              <AccountTabTab
                selected={value === TabValue.CONTACTS}
                onClick={() => setValue(TabValue.CONTACTS)}
                icon={
                  <IconMContact
                    color={
                      value === TabValue.CONTACTS
                        ? colors.darkGreen
                        : colors.black
                    }
                  />
                }
                label="Contacts"
                endIcon={
                  account?.totalProspects != null && (
                    <Badge>{account?.totalProspects}</Badge>
                  )
                }
              />
              {hasOpportunities && (
                <>
                  <div
                    className="tabs-divider"
                    style={{
                      visibility:
                        value === TabValue.CONTACTS ||
                        value === TabValue.OPPORTUNITIES
                          ? 'hidden'
                          : 'visible',
                    }}
                  />
                  <AccountTabTab
                    selected={value === TabValue.OPPORTUNITIES}
                    onClick={() => {
                      if (showUpgradeBadges) {
                        openPaywallIfNeeded();
                      } else {
                        setValue(TabValue.OPPORTUNITIES);
                      }
                    }}
                    icon={
                      <IconPriceTag
                        color={
                          value === TabValue.OPPORTUNITIES
                            ? colors.darkGreen
                            : colors.black
                        }
                      />
                    }
                    label="Deals"
                    endIcon={
                      <div className="flex items-center">
                        {account?.totalOpportunities != null && (
                          <Badge>{account?.totalOpportunities}</Badge>
                        )}
                        <UpgradeLabel style={{ marginLeft: 4 }} />
                      </div>
                    }
                  />
                </>
              )}
              {user?.otherSettings?.hasTasks && (
                <>
                  <div
                    className="tabs-divider"
                    style={{
                      visibility:
                        (!hasOpportunities && value === TabValue.CONTACTS) ||
                        (hasOpportunities &&
                          value === TabValue.OPPORTUNITIES) ||
                        value === TabValue.TIMELINE
                          ? 'hidden'
                          : 'visible',
                    }}
                  />
                  <AccountTabTab
                    selected={value === TabValue.TIMELINE}
                    onClick={() => setValue(TabValue.TIMELINE)}
                    icon={
                      <TimelineMdIcon
                        className={
                          value === TabValue.TIMELINE
                            ? 'text-primary-800'
                            : 'text-gray-900'
                        }
                      />
                    }
                    label="Timeline"
                  />
                </>
              )}
            </AccountTabsContainer>
          </div>
          <Suspense
            fallback={
              <div className="size-full grid place-content-center">
                <DotSpinner />
              </div>
            }
          >
            {renderTabContent()}
          </Suspense>
        </>
      )}
      {/* Kept outside the loading/not-existing ternary — an account refetch
          mid-task (e.g. a contact re-association) must not unmount these and
          lose the in-progress draft/recording/notes. Each is portaled via
          useTaskViewPortal; key={taskSessionId} still remounts on a new task. */}
      {modalVisible === 'email' && (
        <>
          <div ref={emailPortal.setHomeNode} className="contents" />
          <Suspense
            fallback={
              <div className="size-full grid place-content-center">
                <DotSpinner />
              </div>
            }
          >
            {emailPortal.isConnected &&
              createPortal(
                <EmailView
                  key={taskSessionId}
                  task={task}
                  selectedProspectId={taskProspectId}
                  selectedThreadId={selectedThreadId}
                  callOnSuccess={callOnSuccess}
                  suggestionId={suggestionId}
                  handleCloseModal={handleCloseModal}
                  defaultOpenTemplateTab={defaultOpenTemplateTab}
                  visibility="visible"
                />,
                emailPortal.portalContainer,
              )}
          </Suspense>
        </>
      )}
      {modalVisible === 'linkedin' && taskProspectId && selectedAccountId && (
        <>
          <div ref={linkedinPortal.setHomeNode} className="contents" />
          <Suspense
            fallback={
              <div className="size-full grid place-content-center">
                <DotSpinner />
              </div>
            }
          >
            {linkedinPortal.isConnected &&
              createPortal(
                <LinkedinView
                  key={taskSessionId}
                  task={task}
                  selectedProspectId={taskProspectId}
                  selectedAccountId={selectedAccountId}
                  callOnSuccess={callOnSuccess}
                  suggestionId={suggestionId}
                  handleCloseModal={handleCloseModal}
                  defaultOpenTemplateTab={defaultOpenTemplateTab}
                  visibility="visible"
                  close={close}
                  setLinkedinTabLabel={setLinkedinTabLabel}
                />,
                linkedinPortal.portalContainer,
              )}
          </Suspense>
        </>
      )}
      {modalVisible === 'call' && (
        <>
          <div ref={callPortal.setHomeNode} className="contents" />
          <Suspense
            fallback={
              <div className="size-full grid place-content-center">
                <DotSpinner />
              </div>
            }
          >
            {callPortal.isConnected &&
              createPortal(
                <CallView
                  key={taskSessionId}
                  selectedProspectId={taskProspectId}
                  task={task}
                  callOnSuccess={callOnSuccess}
                  suggestionId={suggestionId}
                  handleCloseModal={handleCloseModal}
                  onPendingChangesChange={setCallViewIsDirty}
                  visibility="visible"
                  initialPhoneSelected={initialPhoneSelected}
                />,
                callPortal.portalContainer,
              )}
          </Suspense>
        </>
      )}
      {modalVisible === 'inmail' && (
        <>
          <div ref={inmailPortal.setHomeNode} className="contents" />
          <Suspense
            fallback={
              <div className="size-full grid place-content-center">
                <DotSpinner />
              </div>
            }
          >
            {inmailPortal.isConnected &&
              createPortal(
                <InMailView
                  key={taskSessionId}
                  task={task}
                  selectedProspectId={taskProspectId}
                  callOnSuccess={callOnSuccess}
                  suggestionId={suggestionId}
                  handleCloseModal={handleCloseModal}
                  defaultOpenTemplateTab={defaultOpenTemplateTab}
                  visibility="visible"
                  close={close}
                />,
                inmailPortal.portalContainer,
              )}
          </Suspense>
        </>
      )}
    </AccountTabWrapper>
  );
};

export default AccountDrawer;
